/**
 * Sablon / Branding sekmesi.
 *
 * Iki bolum:
 * 1) Kendi sirketinin override'i (CompanyId = userDetails.companyId)
 * 2) Global default (CompanyId = NULL) — sadece SuperAdmin gorebilir/duzenleyebilir
 *
 * Ayrica preview butonu — Render edilen HTML'i yeni sekmede gosterir.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import CollectionsIcon from '@mui/icons-material/Collections';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from '../../../../contexts/AuthContext';
import ImageLibrarySelectionModal from '../../inventory/ImageLibrarySelectionModal';
import type {
  ReportSchedule,
  ReportTemplateSettings,
  ReportTemplateSettingsUpsertRequest,
} from '../types';
import {
  fetchPreviewHtml,
  getTemplateSettings,
  listSchedules,
  upsertTemplateSettings,
} from '../services/mailReportsApi';
import { humanizeCron } from '../utils/cronHumanizer';

interface FormState {
  BrandName: string;
  PrimaryColor: string;
  PrimaryDarkColor: string;
  AccentColor: string;
  LogoUrl: string;
  FooterText: string;
  FullHtmlOverride: string;
}

const DEFAULT_FORM: FormState = {
  BrandName: 'AQUASOFT',
  PrimaryColor: '#4f46e5',
  PrimaryDarkColor: '#1e1b4b',
  AccentColor: '#7c3aed',
  LogoUrl: '',
  FooterText: '',
  FullHtmlOverride: '',
};

const TemplateSettingsTab: React.FC = () => {
  const { userDetails, companyName, getBranchName, isSuperAdmin } = useAuth();

  const [companyForm, setCompanyForm] = React.useState<FormState>(DEFAULT_FORM);
  const [globalForm, setGlobalForm] = React.useState<FormState>(DEFAULT_FORM);
  const [companyEntity, setCompanyEntity] = React.useState<ReportTemplateSettings | null>(null);
  const [globalEntity, setGlobalEntity] = React.useState<ReportTemplateSettings | null>(null);

  const [schedules, setSchedules] = React.useState<ReportSchedule[]>([]);
  const [previewScheduleId, setPreviewScheduleId] = React.useState<number | ''>('');
  /**
   * Hangi şubenin önizlemesi yapılacak.
   *  - 'all'   : "Tüm Şubeler" birleşik özet (multi-branch summary tablosu)
   *  - <id>    : Tek bir şubenin raporu (zamanlamadaki şubelerden biri)
   * Multi-branch schedule'da kullanıcı dropdown'dan seçer; tek-şube veya
   * "tüm şubeler" zamanlamasında otomatik default'a düşer.
   */
  const [previewBranchId, setPreviewBranchId] = React.useState<string>('all');
  const [previewLoading, setPreviewLoading] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [savingScope, setSavingScope] = React.useState<'company' | 'global' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; severity: 'success' | 'error'; message: string }>(
    { open: false, severity: 'success', message: '' }
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Kendi sirketinin ayari
      const own = await getTemplateSettings(userDetails?.companyId || null);
      setCompanyEntity(own);
      setCompanyForm(own ? entityToForm(own) : DEFAULT_FORM);

      // Global default (SuperAdmin'e gosteriliyor)
      if (isSuperAdmin) {
        try {
          const glob = await getTemplateSettings('global');
          setGlobalEntity(glob);
          setGlobalForm(glob ? entityToForm(glob) : DEFAULT_FORM);
        } catch {
          // 403 olabilir — gizle
        }
      }

      // Onizleme dropdown'i icin zamanlamalari yukle (sirketin kendi zamanlamalari)
      try {
        const list = await listSchedules(false);
        setSchedules(list);
        // Default secim: ilk aktif zamanlama; o da yoksa ilk olanı.
        const firstActive = list.find((s) => s.IsActive) || list[0];
        if (firstActive) setPreviewScheduleId(firstActive.Id);
      } catch {
        // Schedule listesi cekilemese de tab calismali
      }
    } catch (err: any) {
      setError(err?.response?.data?.Message || err?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [userDetails?.companyId, isSuperAdmin]);

  React.useEffect(() => {
    load();
  }, [load]);

  const save = async (scope: 'company' | 'global') => {
    const form = scope === 'company' ? companyForm : globalForm;
    const targetCompanyId = scope === 'company' ? userDetails?.companyId || '' : null;

    if (scope === 'company' && !targetCompanyId) {
      setSnackbar({ open: true, severity: 'error', message: 'Şirket ID bulunamadı' });
      return;
    }

    const payload: ReportTemplateSettingsUpsertRequest = {
      CompanyId: targetCompanyId,
      BrandName: form.BrandName.trim() || 'AQUASOFT',
      PrimaryColor: form.PrimaryColor || '#4f46e5',
      PrimaryDarkColor: form.PrimaryDarkColor || '#1e1b4b',
      AccentColor: form.AccentColor || '#7c3aed',
      LogoUrl: form.LogoUrl || '',
      FooterText: form.FooterText || '',
      FullHtmlOverride: form.FullHtmlOverride || null,
    };

    setSavingScope(scope);
    try {
      const result = await upsertTemplateSettings(payload);
      if (scope === 'company') {
        setCompanyEntity(result);
      } else {
        setGlobalEntity(result);
      }
      setSnackbar({ open: true, severity: 'success', message: 'Şablon kaydedildi.' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Kaydedilemedi',
      });
    } finally {
      setSavingScope(null);
    }
  };

  /**
   * Schedule seçimi değişince, branch picker'ın default'unu o schedule'a göre ayarla.
   *  - BranchIds boş -> 'all' (Tüm Şubeler birleşik özet)
   *  - Tek şube -> direkt o şube
   *  - Çoklu şube -> ilk şube (kullanıcı isterse değiştirir)
   */
  React.useEffect(() => {
    const selected = schedules.find((s) => s.Id === previewScheduleId) || null;
    if (!selected || selected.BranchIds.length === 0) {
      setPreviewBranchId('all');
    } else {
      setPreviewBranchId(selected.BranchIds[0]);
    }
  }, [previewScheduleId, schedules]);

  // Seçili schedule (branch picker'ın görünürlüğü için)
  const selectedSchedule = React.useMemo(
    () => schedules.find((s) => s.Id === previewScheduleId) || null,
    [schedules, previewScheduleId],
  );

  /**
   * Onizleme — secilen zamanlamanin (schedule) ReportType + CronExpression + TimeZoneId
   * + sube bilgilerine gore mailin birebir aynisini gosterir.
   * Cron varsa backend cron'a gore periyot hesaplar (haftalikta gecen pazartesi -> bu pazartesi gibi);
   * cron yoksa gunluk fallback (dun 00:00 -> bugun 00:00).
   */
  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const selected = schedules.find((s) => s.Id === previewScheduleId) || null;

      // Schedule yok / secilmemis ise: eski "Dunun Datasi" davranisi (calendar-gun fallback).
      const reportType = selected?.ReportType || 'daily';
      const cronExpression = selected?.CronExpression || null;
      const timeZoneId = selected?.TimeZoneId || 'Turkey Standard Time';

      // Branch seçimi:
      //  - 'all' -> null (backend "Tüm Şubeler" birleşik özetini render eder)
      //  - <id>  -> sadece o şubenin raporu
      // Schedule yoksa veya BranchIds boşsa otomatik 'all' kullanılır.
      let branchId: string | null = null;
      let branchName: string | null = null;
      if (selected && selected.BranchIds.length > 0 && previewBranchId !== 'all') {
        branchId = previewBranchId;
        branchName = getBranchName(branchId) || branchId;
      }

      // Frontend startDate/endDate fallback (cron yoksa kullanilir).
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const startIso = new Date(yesterday.setHours(0, 0, 0, 0)).toISOString();
      const endIso = new Date(today.setHours(0, 0, 0, 0)).toISOString();

      const html = await fetchPreviewHtml({
        startDate: startIso,
        endDate: endIso,
        reportType,
        cronExpression,
        timeZoneId,
        branchId,
        branchName,
      });

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Önizleme yüklenemedi',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>Mail Şablonu / Branding</Typography>
          <Typography variant="caption" color="text.secondary">
            Marka adı, renk ve logo gibi bileşenler tüm mail raporlarında görüntülenir.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="preview-schedule-label">Önizlenecek Zamanlama</InputLabel>
            <Select
              labelId="preview-schedule-label"
              label="Önizlenecek Zamanlama"
              value={previewScheduleId}
              onChange={(e) => setPreviewScheduleId(e.target.value as number | '')}
              displayEmpty
              renderValue={(val: unknown) => {
                if (val === '' || val === null || val === undefined) {
                  return <em>Zamanlama yok — Dünün Datası (günlük)</em>;
                }
                const s = schedules.find((x) => x.Id === Number(val));
                if (!s) return '—';
                const typeLabel = s.ReportType === 'weekly' ? 'Haftalık' : s.ReportType === 'monthly' ? 'Aylık' : 'Günlük';
                return `${s.Name || 'İsimsiz Rapor'} • ${typeLabel}`;
              }}
            >
              {schedules.length === 0 && (
                <MenuItem value="" disabled>
                  <em>Henüz zamanlama yok</em>
                </MenuItem>
              )}
              {schedules.length > 0 && (
                <ListSubheader sx={{ background: '#fafafa', lineHeight: '32px' }}>
                  Zamanlamalar
                </ListSubheader>
              )}
              {schedules.map((s) => {
                const typeLabel =
                  s.ReportType === 'weekly' ? 'Haftalık' : s.ReportType === 'monthly' ? 'Aylık' : 'Günlük';
                const human = humanizeCron(s.CronExpression);
                const branchSummary =
                  s.BranchIds.length === 0 ? 'Tüm Şubeler' : `${s.BranchIds.length} şube`;
                return (
                  <MenuItem key={s.Id} value={s.Id}>
                    <Stack sx={{ width: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={s.Name ? 'text.primary' : 'text.secondary'}
                          fontStyle={s.Name ? 'normal' : 'italic'}
                        >
                          {s.Name || 'İsimsiz Rapor'}
                        </Typography>
                        <Chip
                          size="small"
                          label={typeLabel}
                          color={s.ReportType === 'weekly' ? 'secondary' : s.ReportType === 'monthly' ? 'info' : 'primary'}
                          sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                        />
                        {!s.IsActive && (
                          <Chip size="small" label="Pasif" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {human} • {branchSummary}
                      </Typography>
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {/* Multi-branch schedule seçildiyse hangi şubenin önizleneceğini sor */}
          {selectedSchedule && selectedSchedule.BranchIds.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="preview-branch-label">Şube Önizleme</InputLabel>
              <Select
                labelId="preview-branch-label"
                label="Şube Önizleme"
                value={previewBranchId}
                onChange={(e) => setPreviewBranchId(e.target.value)}
              >
                {selectedSchedule.BranchIds.length > 1 && (
                  <MenuItem value="all">
                    <em>Tüm Şubeler (birleşik özet)</em>
                  </MenuItem>
                )}
                {selectedSchedule.BranchIds.map((bid) => (
                  <MenuItem key={bid} value={bid}>
                    {getBranchName(bid) || bid}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
            onClick={handlePreview}
            disabled={previewLoading}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {previewLoading ? 'Yükleniyor...' : 'Mail Önizle'}
          </Button>
        </Stack>
      </Stack>

      {/* Sirket-Ozel */}
      <FormSection
        title={companyName ? `${companyName} — Şirkete Özel Şablon` : 'Şirkete Özel Şablon'}
        description={
          companyEntity
            ? `Son güncelleme: ${new Date(companyEntity.UpdatedAt).toLocaleString('tr-TR')} — ${companyEntity.UpdatedByEmail || ''}`
            : 'Henüz şirkete özel ayar yok; global default kullanılıyor. Kaydederseniz tüm mail raporlarınız bu ayarları kullanır.'
        }
        form={companyForm}
        onChange={setCompanyForm}
        onSave={() => save('company')}
        saving={savingScope === 'company'}
      />

      {isSuperAdmin && (
        <>
          <Divider sx={{ my: 4 }}>
            <Typography variant="caption" color="text.secondary">
              SUPERADMIN BÖLÜMÜ
            </Typography>
          </Divider>

          <FormSection
            title="Global Default (Tüm Şirketler)"
            description={
              globalEntity
                ? `Son güncelleme: ${new Date(globalEntity.UpdatedAt).toLocaleString('tr-TR')} — ${globalEntity.UpdatedByEmail || ''}`
                : 'Henüz global default ayar yok.'
            }
            form={globalForm}
            onChange={setGlobalForm}
            onSave={() => save('global')}
            saving={savingScope === 'global'}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function entityToForm(e: ReportTemplateSettings): FormState {
  return {
    BrandName: e.BrandName,
    PrimaryColor: e.PrimaryColor,
    PrimaryDarkColor: e.PrimaryDarkColor,
    AccentColor: e.AccentColor,
    LogoUrl: e.LogoUrl,
    FooterText: e.FooterText,
    FullHtmlOverride: e.FullHtmlOverride || '',
  };
}

// ---------------------------------------------------------------------------

interface FormSectionProps {
  title: string;
  description: string;
  form: FormState;
  onChange: (next: FormState) => void;
  onSave: () => void;
  saving: boolean;
}

const FormSection: React.FC<FormSectionProps> = ({ title, description, form, onChange, onSave, saving }) => {
  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    onChange({ ...form, [key]: value });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={saving}>
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Marka Adı"
            value={form.BrandName}
            onChange={(e) => updateField('BrandName', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LogoUrlField value={form.LogoUrl} onChange={(v) => updateField('LogoUrl', v)} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <ColorField
            label="Primary Renk"
            value={form.PrimaryColor}
            onChange={(v) => updateField('PrimaryColor', v)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ColorField
            label="Primary Koyu"
            value={form.PrimaryDarkColor}
            onChange={(v) => updateField('PrimaryDarkColor', v)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ColorField
            label="Aksan Renk"
            value={form.AccentColor}
            onChange={(v) => updateField('AccentColor', v)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Footer Metni"
            value={form.FooterText}
            onChange={(e) => updateField('FooterText', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Tam HTML Override (opsiyonel, ileri seviye)"
            value={form.FullHtmlOverride}
            onChange={(e) => updateField('FullHtmlOverride', e.target.value)}
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            helperText="Boş = standart şablon. Doldurulursa kendi HTML'iniz kullanılır. Placeholder'lar: {{BRAND_NAME}}, {{LOGO_URL}}, {{PRIMARY_COLOR}}, {{PERIOD_LABEL}}, {{BRANCH_NAME}}, {{TOTAL_REVENUE}}, {{ORDER_COUNT}}, {{AVERAGE_BASKET}}, {{GENERATED_AT}}, {{FOOTER_TEXT}}"
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Logo URL alani — manuel URL veya resim kutuphanesinden secim
// ---------------------------------------------------------------------------

interface LogoUrlFieldProps {
  value: string;
  onChange: (next: string) => void;
}

const LogoUrlField: React.FC<LogoUrlFieldProps> = ({ value, onChange }) => {
  const { userDetails } = useAuth();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);

  React.useEffect(() => {
    setImgError(false);
  }, [value]);

  const handlePicked = (urls: string[]) => {
    if (urls.length > 0) {
      onChange(urls[0]);
    }
    setPickerOpen(false);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label="Logo URL"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          placeholder="https://... veya kütüphaneden seç"
          InputProps={{
            endAdornment: value ? (
              <IconButton size="small" onClick={() => onChange('')} title="Logoyu temizle">
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<CollectionsIcon />}
          onClick={() => setPickerOpen(true)}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Kütüphane
        </Button>
        {value && !imgError && (
          <Box
            sx={{
              width: 56,
              height: 40,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              background: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={value}
              alt="logo"
              onError={() => setImgError(true)}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}
      </Stack>

      <ImageLibrarySelectionModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSave={handlePicked}
        companyId={userDetails?.companyId || null}
        productName="Mail Logo"
        currentImageUrls={value ? [value] : []}
        productId={null}
        singleSelect
        maxSelection={1}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Renk secici — TextField + native HTML5 color input
// ---------------------------------------------------------------------------

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
}

const ColorField: React.FC<ColorFieldProps> = ({ label, value, onChange }) => {
  const colorRef = React.useRef<HTMLInputElement>(null);
  const safeColor = /^#([0-9a-f]{3}){1,2}$/i.test(value) ? value : '#4f46e5';

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      InputProps={{
        startAdornment: (
          <Box
            onClick={() => colorRef.current?.click()}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              border: '1px solid #ddd',
              bgcolor: safeColor,
              mr: 1,
              cursor: 'pointer',
              flexShrink: 0,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            <Box
              component="input"
              ref={colorRef}
              type="color"
              value={safeColor}
              onChange={(e: any) => onChange(e.target.value)}
              sx={{
                opacity: 0,
                width: 0,
                height: 0,
                border: 0,
                p: 0,
                m: 0,
                position: 'absolute',
                pointerEvents: 'none',
              }}
            />
          </Box>
        ),
      }}
    />
  );
};

export default TemplateSettingsTab;
